/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PositionReportSource, VehicleStatus } from 'src/graphql/generated/graphql.generated';
import {
  WaypointSummaryRecord,
  WaypointSummaryProps,
} from 'src/store/generated/waypoint-summary-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleDispositionProps {
  id: string;
  vehicleId: string;
  status: VehicleStatus;
  currentWaypointId: string | null;
  currentTripLegId: string | null;
  currentTripId: string | null;
  lastTripId: string | null;
  currentAgencyLocationId: string | null;
  notes: string | null;
  isAssetOutOfService: boolean | null;
  lastQualifyingPositionReportTime: Date | null;
  latitude: number | null;
  longitude: number | null;
  etlo: Date | null;
  statusDescription: string | null;
  from: WaypointSummaryProps | null;
  to: WaypointSummaryProps | null;
  positionTime: Date | null;
  positionSource: PositionReportSource | null;
  vehiclePositionSummaryId: string | null;
  latestPositionReportId: string | null;
  latestFlightFollowingReportId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleDispositionRecord extends Record<VehicleDispositionProps>({
  id: null as any,
  vehicleId: null as any,
  status: null as any,
  currentWaypointId: null as any,
  currentTripLegId: null as any,
  currentTripId: null as any,
  lastTripId: null as any,
  currentAgencyLocationId: null as any,
  notes: null as any,
  isAssetOutOfService: null as any,
  lastQualifyingPositionReportTime: null as any,
  latitude: null as any,
  longitude: null as any,
  etlo: null as any,
  statusDescription: null as any,
  from: null as any,
  to: null as any,
  positionTime: null as any,
  positionSource: null as any,
  vehiclePositionSummaryId: null as any,
  latestPositionReportId: null as any,
  latestFlightFollowingReportId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    vehicleId: string;
    status: VehicleStatus;
    currentWaypointId: string | null;
    currentTripLegId: string | null;
    currentTripId: string | null;
    lastTripId: string | null;
    currentAgencyLocationId: string | null;
    notes: string | null;
    isAssetOutOfService: boolean | null;
    lastQualifyingPositionReportTime: string | number | Date | null;
    latitude: number | null;
    longitude: number | null;
    etlo: string | number | Date | null;
    statusDescription: string | null;
    from: any;
    to: any;
    positionTime: string | number | Date | null;
    positionSource: PositionReportSource | null;
    vehiclePositionSummaryId: string | null;
    latestPositionReportId: string | null;
    latestFlightFollowingReportId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleDispositionRecord {
    if (json == null) {
      throw new Error('VehicleDispositionRecord.fromJS: parameter cannot be null');
    }
    return new VehicleDispositionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleDispositionJson.id',
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'VehicleDispositionJson.vehicleId',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', VehicleStatus.AtWaypoint),
        'VehicleDispositionJson.status',
      ),
      currentWaypointId: json.currentWaypointId,
      currentTripLegId: json.currentTripLegId,
      currentTripId: json.currentTripId,
      lastTripId: json.lastTripId,
      currentAgencyLocationId: json.currentAgencyLocationId,
      notes: json.notes,
      isAssetOutOfService: json.isAssetOutOfService,
      lastQualifyingPositionReportTime:
        json.lastQualifyingPositionReportTime != null
          ? new Date(json.lastQualifyingPositionReportTime)
          : null,
      latitude: json.latitude,
      longitude: json.longitude,
      etlo: json.etlo != null ? new Date(json.etlo) : null,
      statusDescription: json.statusDescription,
      from: json.from != null ? new WaypointSummaryRecord(json.from) : null,
      to: json.to != null ? new WaypointSummaryRecord(json.to) : null,
      positionTime: json.positionTime != null ? new Date(json.positionTime) : null,
      positionSource: json.positionSource,
      vehiclePositionSummaryId: json.vehiclePositionSummaryId,
      latestPositionReportId: json.latestPositionReportId,
      latestFlightFollowingReportId: json.latestFlightFollowingReportId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleDispositionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    vehicleId: string;
    status: VehicleStatus;
    currentWaypointId: string | null;
    currentTripLegId: string | null;
    currentTripId: string | null;
    lastTripId: string | null;
    currentAgencyLocationId: string | null;
    notes: string | null;
    isAssetOutOfService: boolean | null;
    lastQualifyingPositionReportTime: string | number | Date | null;
    latitude: number | null;
    longitude: number | null;
    etlo: string | number | Date | null;
    statusDescription: string | null;
    from: any;
    to: any;
    positionTime: string | number | Date | null;
    positionSource: PositionReportSource | null;
    vehiclePositionSummaryId: string | null;
    latestPositionReportId: string | null;
    latestFlightFollowingReportId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleDispositionRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleDispositionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleDispositionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleDispositionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'VehicleDispositionJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', VehicleStatus.AtWaypoint),
            'VehicleDispositionJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentWaypointId: tryOrDefault(
        () => json.currentWaypointId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentTripLegId: tryOrDefault(
        () => json.currentTripLegId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentTripId: tryOrDefault(
        () => json.currentTripId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastTripId: tryOrDefault(
        () => json.lastTripId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentAgencyLocationId: tryOrDefault(
        () => json.currentAgencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAssetOutOfService: tryOrDefault(
        () => json.isAssetOutOfService,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastQualifyingPositionReportTime: tryOrDefault(
        () =>
          json.lastQualifyingPositionReportTime != null
            ? new Date(json.lastQualifyingPositionReportTime)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latitude: tryOrDefault(
        () => json.latitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      longitude: tryOrDefault(
        () => json.longitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etlo: tryOrDefault(
        () => (json.etlo != null ? new Date(json.etlo) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      statusDescription: tryOrDefault(
        () => json.statusDescription,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      from: tryOrDefault(
        () => (json.from != null ? new WaypointSummaryRecord(json.from) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      to: tryOrDefault(
        () => (json.to != null ? new WaypointSummaryRecord(json.to) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionTime: tryOrDefault(
        () => (json.positionTime != null ? new Date(json.positionTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionSource: tryOrDefault(
        () => json.positionSource,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehiclePositionSummaryId: tryOrDefault(
        () => json.vehiclePositionSummaryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestPositionReportId: tryOrDefault(
        () => json.latestPositionReportId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestFlightFollowingReportId: tryOrDefault(
        () => json.latestFlightFollowingReportId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleDispositionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleDispositionProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleDispositionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleDispositionViewModelBase implements VehicleDispositionProps {
  protected readonly _props: VehicleDispositionProps;
  private _json?: VehicleDispositionProps;

  get id(): string {
    return this._props.id;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get status(): VehicleStatus {
    return this._props.status;
  }
  get currentWaypointId(): string | null {
    return this._props.currentWaypointId;
  }
  get currentTripLegId(): string | null {
    return this._props.currentTripLegId;
  }
  get currentTripId(): string | null {
    return this._props.currentTripId;
  }
  get lastTripId(): string | null {
    return this._props.lastTripId;
  }
  get currentAgencyLocationId(): string | null {
    return this._props.currentAgencyLocationId;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get isAssetOutOfService(): boolean | null {
    return this._props.isAssetOutOfService;
  }
  get lastQualifyingPositionReportTime(): Date | null {
    return this._props.lastQualifyingPositionReportTime;
  }
  get latitude(): number | null {
    return this._props.latitude;
  }
  get longitude(): number | null {
    return this._props.longitude;
  }
  get etlo(): Date | null {
    return this._props.etlo;
  }
  get statusDescription(): string | null {
    return this._props.statusDescription;
  }
  get from(): WaypointSummaryProps | null {
    return this._props.from;
  }
  get to(): WaypointSummaryProps | null {
    return this._props.to;
  }
  get positionTime(): Date | null {
    return this._props.positionTime;
  }
  get positionSource(): PositionReportSource | null {
    return this._props.positionSource;
  }
  get vehiclePositionSummaryId(): string | null {
    return this._props.vehiclePositionSummaryId;
  }
  get latestPositionReportId(): string | null {
    return this._props.latestPositionReportId;
  }
  get latestFlightFollowingReportId(): string | null {
    return this._props.latestFlightFollowingReportId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAtWaypoint() {
    return this._props.status === VehicleStatus.AtWaypoint;
  }
  public get isEnRoute() {
    return this._props.status === VehicleStatus.EnRoute;
  }
  public get isGpsAuto() {
    return this._props.positionSource === PositionReportSource.GpsAuto;
  }
  public get isGpsAutoAdsb() {
    return this._props.positionSource === PositionReportSource.GpsAutoAdsb;
  }
  public get isGpsManual() {
    return this._props.positionSource === PositionReportSource.GpsManual;
  }
  public get isPilot() {
    return this._props.positionSource === PositionReportSource.Pilot;
  }
  public get isTCS() {
    return this._props.positionSource === PositionReportSource.TCS;
  }

  public isAnyStatus(...vehicleStatus: VehicleStatus[]) {
    return vehicleStatus.includes(this._props.status);
  }
  public isAnyPositionSource(...positionReportSource: PositionReportSource[]) {
    return (
      this._props.positionSource != null &&
      positionReportSource.includes(this._props.positionSource)
    );
  }

  constructor(props: VehicleDispositionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        vehicleId: this._props.vehicleId,
        status: this._props.status,
        currentWaypointId: this._props.currentWaypointId,
        currentTripLegId: this._props.currentTripLegId,
        currentTripId: this._props.currentTripId,
        lastTripId: this._props.lastTripId,
        currentAgencyLocationId: this._props.currentAgencyLocationId,
        notes: this._props.notes,
        isAssetOutOfService: this._props.isAssetOutOfService,
        lastQualifyingPositionReportTime: this._props.lastQualifyingPositionReportTime,
        latitude: this._props.latitude,
        longitude: this._props.longitude,
        etlo: this._props.etlo,
        statusDescription: this._props.statusDescription,
        from: this._props.from,
        to: this._props.to,
        positionTime: this._props.positionTime,
        positionSource: this._props.positionSource,
        vehiclePositionSummaryId: this._props.vehiclePositionSummaryId,
        latestPositionReportId: this._props.latestPositionReportId,
        latestFlightFollowingReportId: this._props.latestFlightFollowingReportId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleDispositionEntities = (state: AppState) =>
  state.entityStores.vehicleDisposition.entities;

export const selectVehicleDispositionById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleDisposition.entities.get(id);

export const selectVehicleDispositionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleDisposition.meta.get(id);

export const selectVehicleDispositionAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleDispositionById(id),
    selectVehicleDispositionMetadataById(id),
    (vehicleDisposition, meta) => ({ vehicleDisposition, meta }),
  );

export function selectVehicleDispositionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleDispositionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleDispositionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleDispositionEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByStatus(status: VehicleStatus) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByCurrentWaypointId(currentWaypointId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.currentWaypointId === currentWaypointId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByCurrentTripLegId(currentTripLegId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.currentTripLegId === currentTripLegId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByCurrentTripId(currentTripId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.currentTripId === currentTripId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLastTripId(lastTripId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.lastTripId === lastTripId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByCurrentAgencyLocationId(
  currentAgencyLocationId: string,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.currentAgencyLocationId === currentAgencyLocationId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByNotes(notes: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByIsAssetOutOfService(
  isAssetOutOfService: boolean,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.isAssetOutOfService === isAssetOutOfService).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLastQualifyingPositionReportTime(
  lastQualifyingPositionReportTime: Date,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities
      .filter((e) => e.lastQualifyingPositionReportTime === lastQualifyingPositionReportTime)
      .valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLatitude(latitude: number) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.latitude === latitude).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLongitude(longitude: number) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.longitude === longitude).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByEtlo(etlo: Date) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.etlo === etlo).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByStatusDescription(statusDescription: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.statusDescription === statusDescription).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByPositionTime(positionTime: Date) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.positionTime === positionTime).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByPositionSource(
  positionSource: PositionReportSource,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.positionSource === positionSource).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByVehiclePositionSummaryId(
  vehiclePositionSummaryId: string,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.vehiclePositionSummaryId === vehiclePositionSummaryId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLatestPositionReportId(
  latestPositionReportId: string,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.latestPositionReportId === latestPositionReportId).valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByLatestFlightFollowingReportId(
  latestFlightFollowingReportId: string,
) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities
      .filter((e) => e.latestFlightFollowingReportId === latestFlightFollowingReportId)
      .valueSeq(),
  );
}

export function selectVehicleDispositionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleDispositionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
